import React, { forwardRef, RefObject, useCallback, useState } from 'react';
import classNames from 'classnames';
import BaseButtonInner from './BaseButtonInner';
import { isMobileAny } from '../../utils/device';
import classes from './styles/index.module.scss';
import { BaseButtonProps } from './types';
import { DirtyFormProxyButtonProps } from '../DirtyFormProxy/types';
import { CloakLoading } from '../CloakLoading';
import { getAppearanceClasses } from './utils';
import { useMounted } from '../../hooks/useMounted';

const DEFAULT_APPLYONELEMENTPROPS = {};

export const BaseButton = forwardRef<
	HTMLButtonElement | HTMLAnchorElement,
	BaseButtonProps & DirtyFormProxyButtonProps
>(
	(
		{
			primary,
			link,
			linkDark,
			danger,
			dangerLink,
			linkLike,

			reset,
			rounded,
			size = 'normal',
			buttonInnerClassName,
			children,
			className,
			startIcon,
			endIcon,
			disabled,
			componentRef,
			block,
			breakWord,
			active,
			ariaLabel,
			title,
			tagName = 'button',
			applyOnElementProps = DEFAULT_APPLYONELEMENTPROPS,
			textAlign,
			verticalAlign,
			iconClassName,
			dataTestId,
			rel,
			roundedLeft,
			roundedRight,
			bold,
			widthContent,
			resetPadding,
			scantyPadding,
			tabIndex = 0,
			isLoading,
			dataFunction,
			style,
		},
		ref,
	) => {
		const hasIconOnly = (startIcon || endIcon) && !children;
		const { mountedRef } = useMounted();
		const [pressed, setPressed] = useState(false);
		const TagName = tagName;
		const buttonClassName = classNames(
			classes.button,
			getAppearanceClasses({
				appearance: {
					primary,
					link,
					linkDark,
					danger,
					dangerLink,
					linkLike,
				},
				state: {
					disabled,
					active,
					pressed,
					canHover: !isMobileAny,
				},
			}),
			classes[`button_${size}`],
			className,
			{
				[classes.button_widthContent]: widthContent,
				[classes.button_rounded]: rounded,
				[classes.button_roundedLeft]: roundedLeft,
				[classes.button_roundedRight]: roundedRight,
				[classes.button_disabled]: disabled || isLoading,
				[classes.button_iconOnly]: hasIconOnly,
				[classes.button_reset]: reset,
				[classes.button_block]: block,
				[classes.button_breakWord]: breakWord,
				[classes.button_textAlignLeft]: textAlign === 'left',
				[classes.button_textAlignRight]: textAlign === 'right',
				[classes.button_verticalAlignTop]: verticalAlign === 'top',
				[classes.button_verticalAlignMiddle]: verticalAlign === 'middle',
				[classes.button_verticalAlignBottom]: verticalAlign === 'bottom',
				[classes.button_bold]: bold,
				[classes.button_resetPadding]: resetPadding,
				[classes.button_scantyPadding]: scantyPadding,
				[classes.button_loading]: isLoading,
				[classes.button_linkLike]: linkLike,
			},
		);

		const currentTabIndex = disabled ? -1 : tabIndex;
		const baseButtonRef = (componentRef || ref) as RefObject<HTMLAnchorElement> & RefObject<HTMLButtonElement>;

		const handleMouseDown = useCallback(
			(e) => {
				if (disabled || active) {
					return;
				}

				setPressed(true);

				setTimeout(() => {
					if (mountedRef.current) {
						setPressed(false);
					}
				}, 100);

				if (typeof applyOnElementProps['onMouseDown'] === 'function') {
					applyOnElementProps['onMouseDown'](e);
				}
			},
			[active, applyOnElementProps, disabled, mountedRef],
		);

		return (
			<TagName
				{...applyOnElementProps}
				className={buttonClassName}
				ref={baseButtonRef}
				aria-label={ariaLabel}
				title={title}
				disabled={disabled}
				data-testid={dataTestId}
				rel={rel}
				tabIndex={currentTabIndex}
				onMouseDown={handleMouseDown}
				style={style}
				data-function={dataFunction}
			>
				{isLoading && (
					<div className={classes.loadingWrapper}>
						<CloakLoading size='small' />
					</div>
				)}
				{!isLoading && (
					<BaseButtonInner
						buttonInnerClassName={buttonInnerClassName}
						disabled={disabled}
						startIcon={startIcon}
						endIcon={endIcon}
						iconClassName={iconClassName}
					>
						{children}
					</BaseButtonInner>
				)}
			</TagName>
		);
	},
);

BaseButton.displayName = 'BaseButton';
